/**
 * Enrich business expense transactions with tax-time fields for reporting.
 */

import type { BusinessExpenseGstStatus, ReportTransactionRow } from '../../types/report.types';
import { businessMerchantKey } from './business-expense-merchant';
import { expenseOutflowAmount } from './transaction-amount-utils';
import type { BusinessExpenseTransactionLike } from './business-expense-dataset';

export interface BusinessExpenseDbTransaction extends BusinessExpenseTransactionLike {
  business_use_percent?: number | null;
  gst_status?: string | null;
  receipt_attached?: boolean | null;
}

const OVERSEAS_SAAS_VENDOR_LABELS = new Set([
  'OpenAI / ChatGPT',
  'Anthropic / Claude',
  'Cursor',
  'Figma',
  'Miro',
  'Lumen5',
  'Perplexity',
  'Readdy AI',
  'Replit',
  'Hostinger',
]);

function parseGstStatus(raw: string | null | undefined): BusinessExpenseGstStatus | undefined {
  if (raw === 'includes_gst' || raw === 'no_gst' || raw === 'unknown') return raw;
  return undefined;
}

export function inferBusinessExpenseGstStatus(
  tx: BusinessExpenseDbTransaction,
  categoryGstApplicable: boolean | undefined,
  vendorLabel: string
): BusinessExpenseGstStatus {
  const explicit = parseGstStatus(tx.gst_status);
  if (explicit) return explicit;
  if (OVERSEAS_SAAS_VENDOR_LABELS.has(vendorLabel)) return 'unknown';
  if (categoryGstApplicable) return 'unknown';
  return 'unknown';
}

export function claimableAmountForTransaction(
  expenseAud: number,
  businessUsePercent: number | null | undefined
): number | null {
  if (businessUsePercent == null || Number.isNaN(businessUsePercent)) return null;
  return (expenseAud * businessUsePercent) / 100;
}

export function enrichBusinessExpenseRow(
  tx: BusinessExpenseDbTransaction,
  baseRow: Omit<
    ReportTransactionRow,
    'vendorLabel' | 'businessUsePercent' | 'gstStatus' | 'receiptAttached' | 'expenseAmountAud' | 'claimableAmountAud'
  >,
  categoryGstApplicable: boolean | undefined
): ReportTransactionRow {
  const vendorLabel = businessMerchantKey(baseRow.description, baseRow.merchant);
  const expenseAmountAud = expenseOutflowAmount(tx);
  const businessUsePercent = tx.business_use_percent ?? null;
  const gstStatus = inferBusinessExpenseGstStatus(tx, categoryGstApplicable, vendorLabel);
  return {
    ...baseRow,
    vendorLabel,
    businessUsePercent,
    gstStatus,
    receiptAttached: Boolean(tx.receipt_attached),
    expenseAmountAud,
    claimableAmountAud: claimableAmountForTransaction(expenseAmountAud, businessUsePercent),
  };
}
